import { useState, useRef, useEffect } from "react";
import { CaretDown, Globe } from "@phosphor-icons/react";
import { useLang } from "@/lib/lang";
import { api } from "@/lib/api";

export default function LanguageSwitcher({ compact = false, dark = false }) {
  const { lang, setLang, LANGS, t } = useLang();
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(["en"]);
  const [enabledLoaded, setEnabledLoaded] = useState(false);
  const ref = useRef(null);

  // Load which languages the site is publicly cleared to display in.
  // English is always enabled. Other languages appear only after admin flips
  // their global status to "published" in /admin/translations.
  useEffect(() => {
    let ok = true;
    api.get("/i18n/status").then((r) => {
      if (ok) {
        setEnabled(r.data.enabled_langs || ["en"]);
        setEnabledLoaded(true);
      }
    }).catch(() => { if (ok) setEnabledLoaded(true); });
    return () => { ok = false; };
  }, []);

  const availableLangs = LANGS.filter((l) => enabled.includes(l.code));
  const current = availableLangs.find((l) => l.code === lang) || LANGS[0];

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // If user's stored lang is no longer allowed, silently reset to English.
  // Only fire once the allowed-lang list has actually loaded — otherwise the
  // seed default `["en"]` would wipe a valid ?lang=ar deep-link before the
  // /i18n/status API has answered.
  useEffect(() => {
    if (!enabledLoaded) return;
    if (lang !== "en" && !enabled.includes(lang)) {
      setLang("en");
    }
  }, [lang, enabled, enabledLoaded, setLang]);

  // Show the switcher whenever there's at least one language (English is always
  // available). All 7 languages appear inside the dropdown — locked ones show a
  // 'Coming soon' badge so users know translation is planned but not yet live.
  if (LANGS.length === 0) return null;

  const baseClass = dark
    ? "text-neutral-300 hover:text-white"
    : "text-neutral-700 hover:text-[#001588]";

  return (
    <div ref={ref} className="relative" data-testid="lang-switcher">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        data-testid="lang-switcher-btn"
        aria-label={t("lang.switchLabel")}
        className={`flex items-center gap-1.5 text-sm ${baseClass} transition-colors`}
      >
        <Globe size={18} />
        {compact ? (
          <span className="uppercase text-xs tracking-wider">{current.code}</span>
        ) : (
          <span className="hidden md:inline">{current.nativeLabel}</span>
        )}
        <CaretDown size={12} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-56 bg-white border border-[#E5E7EB] shadow-lg py-1 z-50"
          data-testid="lang-switcher-menu"
          role="menu"
        >
          {LANGS.map((l) => {
            const isEnabled = enabled.includes(l.code);
            return (
              <button
                key={l.code}
                onClick={() => { if (isEnabled) { setLang(l.code); setOpen(false); } }}
                disabled={!isEnabled}
                data-testid={`lang-option-${l.code}`}
                className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between ${
                  l.code === lang ? "bg-gray-50 text-[#001588] font-semibold"
                  : isEnabled ? "text-gray-700 hover:bg-gray-50"
                  : "text-gray-400 cursor-not-allowed bg-gray-50/40"
                }`}
              >
                <span>{l.nativeLabel}</span>
                {isEnabled ? (
                  <span className="text-xs uppercase text-gray-400">{l.code}</span>
                ) : (
                  <span className="text-[10px] uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5">Coming soon</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
